import React from 'react';
import { AutoComplete, Collapse, Form, Input, Row, Col, Typography } from 'antd';
import { useTranslation } from '../../i18n';

/**
 * 自定义响应(CR):{ status?, content_type?, body }。body 为空 = 未配置(回落到下一级)。
 * 用于页面规则拦截/限速/回源防护/等候室,以及域名级 custom_responses。
 */

export const CONTENT_TYPE_OPTIONS = [
  'text/html; charset=utf-8',
  'application/json',
  'text/plain; charset=utf-8',
];

export const CR_BODY_MAX = 65536;
const CT_RE = /^[A-Za-z0-9!#$&^_.+-]+\/[A-Za-z0-9!#$&^_.+-]+(\s*;\s*[A-Za-z0-9_-]+=[A-Za-z0-9_.-]+)*$/;

export const byteLength = (s) => {
  try {
    return new TextEncoder().encode(String(s || '')).length;
  } catch (e) {
    return String(s || '').length;
  }
};

export const isValidContentType = (v) => !v || (String(v).length <= 128 && CT_RE.test(String(v).trim()));
export const isValidStatus = (v) => {
  if (v === undefined || v === null || v === '' || Number(v) === 0) return true;
  const n = Number(v);
  return Number.isInteger(n) && ((n >= 200 && n <= 299) || (n >= 400 && n <= 599));
};

/** 返回错误文案,无错返回 null */
export const validateCustomResponse = (cr, t) => {
  if (!cr || typeof cr !== 'object') return null;
  if (!isValidStatus(cr.status)) return t('Status code must be 200-299 or 400-599');
  if (!isValidContentType(cr.content_type)) return t('Invalid content type');
  if (byteLength(cr.body) > CR_BODY_MAX) return t('Response body exceeds 64 KB');
  return null;
};

/** 折叠的高级区 */
export const Folded = ({ label, children, defaultOpen = false }) => (
  <Collapse
    ghost
    size="small"
    defaultActiveKey={defaultOpen ? ['f'] : []}
    style={{ marginBottom: 8 }}
    items={[{ key: 'f', label, children }]}
  />
);

export const StatusInput = ({ value, onChange, presets = [], placeholder }) => (
  <AutoComplete
    value={value ? String(value) : ''}
    options={presets.map((c) => ({ value: String(c) }))}
    onChange={(v) => {
      const n = parseInt(String(v).replace(/\D/g, ''), 10);
      onChange?.(Number.isFinite(n) ? n : undefined);
    }}
    placeholder={placeholder}
    style={{ width: '100%' }}
  />
);

const CustomResponseEditor = ({
  value,
  onChange,
  defaultStatus,
  statusPresets,
  showStatus = true,
  waitingRoom = false,
}) => {
  const { t } = useTranslation();
  const cr = value && typeof value === 'object' ? value : {};
  const set = (k, v) => onChange?.({ ...cr, [k]: v });
  const size = byteLength(cr.body);
  const placeholders = waitingRoom
    ? '::EVENT:: ::IP:: ::GEO:: ::RETRY:: ::AHEAD:: ::REFRESH::'
    : '::EVENT:: ::IP:: ::GEO:: ::RETRY::';
  const presets = statusPresets || (defaultStatus ? [defaultStatus] : []);

  return (
    <>
      <Row gutter={16}>
        {showStatus && (
          <Col xs={24} md={8}>
            <Form.Item
              label={t('Status code')}
              validateStatus={isValidStatus(cr.status) ? undefined : 'error'}
              help={isValidStatus(cr.status) ? undefined : t('Status code must be 200-299 or 400-599')}
            >
              <StatusInput
                value={cr.status}
                onChange={(v) => set('status', v)}
                presets={presets}
                placeholder={defaultStatus ? String(defaultStatus) : t('Default')}
              />
            </Form.Item>
          </Col>
        )}
        <Col xs={24} md={showStatus ? 16 : 24}>
          <Form.Item
            label={t('Content type')}
            validateStatus={isValidContentType(cr.content_type) ? undefined : 'error'}
            help={isValidContentType(cr.content_type) ? undefined : t('Invalid content type')}
          >
            <AutoComplete
              value={cr.content_type || ''}
              options={CONTENT_TYPE_OPTIONS.map((v) => ({ value: v }))}
              onChange={(v) => set('content_type', v)}
              placeholder="text/html; charset=utf-8"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        label={t('Response body')}
        tooltip={t('Placeholders: {{list}}', { list: placeholders })}
        validateStatus={size > CR_BODY_MAX ? 'error' : undefined}
        extra={(
          <Typography.Text type={size > CR_BODY_MAX ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
            {(size / 1024).toFixed(1)} / 64 KB
          </Typography.Text>
        )}
      >
        <Input.TextArea
          value={cr.body || ''}
          onChange={(e) => set('body', e.target.value)}
          rows={6}
          maxLength={CR_BODY_MAX}
          placeholder={t('Leave empty to use the default page')}
          style={{ fontFamily: 'monospace', fontSize: 13 }}
        />
      </Form.Item>
    </>
  );
};

export default CustomResponseEditor;
